import { inject, Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { RestService, RestOptions, BrightcoveTranscriptService } from '@tdwb/core';

// eslint-disable-next-line td-wb-rules/cross-lazyload-module-import-rule
import { LearnRestBaseService } from '@tdwb/lazy/learn';

import { LearnQuestion, LearnUserAnswer } from './domain/learn-questionnaire.domain';
import {
  Lesson,
  SuperCourses,
  FeatureContentData,
  LearnEventType
} from './domain/learn.domain';
import {
  LearnEvent,
  LearnEvents,
  LearnEventSort,
  RegistrationRequest,
  RegistrationResponse
} from './domain/learn-event.domain';
import { TradingLessonsData } from './domain/trading-learn.domain';

/**
 * This Service
 */
@Injectable({
  providedIn: 'root'
})
export class LearnRestService extends LearnRestBaseService {
  constructor(
    restService: RestService = inject(RestService),
    brightcoveTranscriptService: BrightcoveTranscriptService = inject(BrightcoveTranscriptService)
  ) {
    super(restService, brightcoveTranscriptService);
  }

  retrieveSuperCourses(selectedCourse?: boolean): Observable<SuperCourses> {
    const parameters: RestOptions = {
      params: { selectedCourse }
    };
    return this.restService.getResource('education.superCourses').get<SuperCourses>(parameters);
  }

  setSelectedSuperCourse(selectedSuperCourseId: string): Observable<void> {
    const parameters = {
      pathVariables: { selectedSuperCourseId }
    } as RestOptions;
    return this.restService.getResource('education.selectedSuperCourse').put<undefined, void>(undefined, parameters);
  }

  questionnaire(): Observable<Array<LearnQuestion>> {
    return this.restService.getResource('education.questionnaire').get<Array<LearnQuestion>>();
  }

  saveUserAnswers(questions: Array<LearnUserAnswer>): Observable<void> {
    return this.restService.getResource('education.saveUserAnswers').put<Array<LearnUserAnswer>, void>(questions);
  }

  fetchLearningPreferences(): Observable<Array<string>> {
    return this.restService.getResource('education.learningPreference').get<Array<string>>();
  }

  addLearningPreference(topic: string): Observable<void> {
    return this.restService.getResource('education.addLearningPreference').post<{ topic: string }, void>({ topic });
  }

  deleteLearningPreference(filter: string): Observable<void> {
    const parameters = {
      params: { filter }
    } as RestOptions;
    return this.restService.getResource('education.deleteLearningPreference').del<void>(undefined, parameters);
  }

  deleteAllLearningPreferences(): Observable<void> {
    return this.restService.getResource('education.deleteLearningPreferences').del<void>(undefined);
  }

  addVideoBookmark(videoId: string): Observable<void> {
    const parameters = {
      pathVariables: { videoId }
    } as RestOptions;
    return this.restService.getResource('education.addVideoBookmark').post(undefined, parameters);
  }

  deleteVideoBookmark(videoId: string): Observable<void> {
    const parameters = {
      pathVariables: { videoId }
    } as RestOptions;
    return this.restService.getResource('education.deleteVideoBookmark').del<void>(undefined, parameters);
  }

  toggleVideoBookMark(lesson: Lesson): Observable<void> {
    return lesson.saved
      ? this.deleteVideoBookmark(lesson.contentMetaData.id)
      : this.addVideoBookmark(lesson.contentMetaData.id);
  }

  retrieveFeatureContent(name: string): Observable<FeatureContentData> {
    const parameters: RestOptions = {
      params: { name }
    };
    return this.restService.getResource('education.group').get<FeatureContentData>(parameters);
  }

  retrieveEvents(type: LearnEventType | undefined, filter: Array<string>, sort?: LearnEventSort,
                 offset?: number, limit?: number, registeredOnly?: boolean): Observable<LearnEvents> {
    const parameters = {
      params: { type, filter, sort, offset, limit, registeredOnly }
    } as RestOptions;

    return this.restService.getResource('on24.getEvents').get<LearnEvents>(parameters);
  }

  registerEvent(eventId: string, registrationRequest: RegistrationRequest): Observable<RegistrationResponse> {
    const parameters = {
      pathVariables: { eventId }
    } as RestOptions;
    return this.restService.getResource('on24.registerUser').post<RegistrationRequest, RegistrationResponse>(registrationRequest, parameters);
  }

  fetchTradingLessons(linkId: string): Observable<TradingLessonsData> {
    const parameters: RestOptions = {
      params: { linkId }
    };
    return this.restService.getResource('education.contextualLink').get<TradingLessonsData>(parameters);
  }

  retrieveEvent(eventId: string): Observable<LearnEvent> {
    const restOptions: RestOptions = {
      pathVariables: { eventId }
    };
    return this.restService.getResource('on24.getEventById').get<LearnEvent>(restOptions)
      .pipe(
        map( (event: LearnEvent): LearnEvent => {
          if (event !== undefined) {
            event.description = this.parseDescription(event.description);
          }
          return event;
        })
      );
  }

  private parseDescription(description: string): string {
    if (description !== undefined) {
      // TODO: need to parse description into multi paragraphs after backend finish parse promotionalSummary
      const dataLines = description.split('\n\n');
      description = dataLines.join(' ');
      return description;
    }
  }
}
